<template>
  <SiteFooter
    :locale="normalizedLocale"
    :override="overrideParsed"
    :theme="resolvedTheme"
  />
</template>

<script setup lang="ts">
/**
 * Web Component root — styles in this file are injected into Shadow DOM.
 * HTML usage:
 *   <gcsa-site-footer locale="zh-CN" theme="dark"></gcsa-site-footer>
 *
 * Theme MUST reach the inner SiteFooter as a class (site-footer-theme--*).
 * iOS Safari often fails to recompute `:host([theme])` / `:host-context`
 * when only the host attribute changes.
 */
import { computed, toRef } from 'vue';
import SiteFooter from './SiteFooter.vue';
import { useCeHostTheme } from '../composables/useCeHostTheme';
import type { GcsaFooterSnapshot, SiteFooterLocale } from '../consts/gcsaFooterContent';

const props = withDefaults(
  defineProps<{
    locale?: string;
    /** JSON string when used as HTML attribute */
    override?: string;
    /** Host theme attribute — reflected + synced for Shadow DOM styles */
    theme?: string;
  }>(),
  {
    locale: 'zh-CN',
    override: '',
    theme: ''
  }
);

const { resolvedTheme } = useCeHostTheme(toRef(props, 'theme'));

const LOCALE_SET = new Set(['zh-CN', 'zh-TW', 'en-US']);

const normalizedLocale = computed<SiteFooterLocale>(() => {
  const value = props.locale || 'zh-CN';
  return LOCALE_SET.has(value) ? (value as SiteFooterLocale) : 'zh-CN';
});

const overrideParsed = computed<Partial<GcsaFooterSnapshot> | undefined>(() => {
  if (!props.override?.trim()) return undefined;
  try {
    return JSON.parse(props.override) as Partial<GcsaFooterSnapshot>;
  } catch {
    return undefined;
  }
});
</script>

<style>
@import '../css/site-footer.css';
</style>
